// src/pages/IncomingMessages.js
import React, { useState, useEffect, useRef } from 'react';
import { ContentLayout, PageContainer } from '../components/layout/Layout';
import { adminAPI } from '../services/api/adminAPI';
import { MessageSquare, Clock, User, Phone, Loader, Send, AlertCircle } from 'lucide-react';
import { WhatsAppMessagePreview } from '../components/WhatsAppMessagePreview';

// Generate a temporary ID for optimistic messages
const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export function IncomingMessages({ onNavigate }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [filterOpenWindow, setFilterOpenWindow] = useState(false); // Filter to show only conversations with open 24h window
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminAPI.getIncomingMessageConversations();
      setConversations(result.conversations || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationMessages = async (senderNumber) => {
    try {
      setLoadingMessages(true);
      setError(null);
      const result = await adminAPI.getConversationMessages(senderNumber);
      setMessages(result.messages || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err.message);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    loadConversationMessages(conversation.senderNumber);
    setMessageInput('');
  };

  // Check if we're within the 24-hour free texting window
  const isWithin24HourWindow = (lastIncomingMessageAt) => {
    if (!lastIncomingMessageAt) return false;
    const lastMessageTime = new Date(lastIncomingMessageAt);
    const now = new Date();
    const diffInHours = (now - lastMessageTime) / (1000 * 60 * 60);
    return diffInHours <= 24;
  };

  // Calculate remaining time in the 24-hour window
  const getRemainingWindowTime = (lastIncomingMessageAt) => {
    if (!lastIncomingMessageAt) return null;
    const lastMessageTime = new Date(lastIncomingMessageAt);
    const windowEnd = new Date(lastMessageTime.getTime() + 24 * 60 * 60 * 1000);
    const now = new Date();
    const remainingMs = windowEnd - now;

    if (remainingMs <= 0) return null;

    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours} שעות ו-${minutes} דקות`;
    }
    return `${minutes} דקות`;
  };

  // Handle sending a free-text message with optimistic UI
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    // Client-side check (backend will verify too)
    if (!isWithin24HourWindow(selectedConversation.lastIncomingMessageAt)) {
      setError('חלון ה-24 שעות פג תוקף. לא ניתן לשלוח הודעה חופשית.');
      return;
    }

    const messageText = messageInput.trim();
    const tempId = generateTempId();

    // Create optimistic message with pending status
    const optimisticMessage = {
      id: tempId,
      messageBody: messageText,
      direction: 'outbound',
      createdAt: new Date().toISOString(),
      status: 'pending'
    };

    // Immediately add the message to UI and clear input
    setMessages(prev => [...prev, optimisticMessage]);
    setMessageInput('');
    setError(null);

    try {
      const result = await adminAPI.sendWhatsAppFreeText({
        phoneNumber: selectedConversation.senderNumber,
        message: messageText
      });

      // Update the message with the real ID and sent status
      setMessages(prev => prev.map(msg =>
        msg.id === tempId
          ? { ...msg, id: result.messageSid, status: result.status || 'sent' }
          : msg
      ));

    } catch (err) {
      console.error('Error sending message:', err);
      // Update the message to show error status
      setMessages(prev => prev.map(msg =>
        msg.id === tempId
          ? { ...msg, status: 'error' }
          : msg
      ));
      setError(err.message);
    }
  };

  // Handle Enter key press in input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    // Remove "whatsapp:" prefix if present
    return phoneNumber.replace('whatsapp:', '');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 24) {
      // Show time for today
      return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays < 7) {
      // Show day of week for this week
      return date.toLocaleDateString('he-IL', { weekday: 'short' });
    } else {
      // Show date for older messages
      return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
    }
  };

  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
      return 'היום';
    } else if (isYesterday) {
      return 'אתמול';
    } else {
      return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    messages.forEach((message) => {
      const messageDate = new Date(message.createdAt).toDateString();
      if (messageDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = messageDate;
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  // Filter conversations based on 24-hour window if filter is enabled
  const filteredConversations = filterOpenWindow
    ? conversations.filter(conv => isWithin24HourWindow(conv.lastIncomingMessageAt))
    : conversations;

  // Count conversations with open window (for filter badge)
  const openWindowCount = conversations.filter(conv => isWithin24HourWindow(conv.lastIncomingMessageAt)).length;

  return (
    <ContentLayout currentPage="incoming-messages" onNavigate={onNavigate}>
      <PageContainer>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-pink-500" />
              הודעות נכנסות WhatsApp
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              צפייה בהודעות נכנסות מלקוחות דרך WhatsApp
            </p>
          </div>

          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Main Content - Split View */}
          <div className="flex-1 flex overflow-hidden">
            {/* Conversations List */}
            <div className="w-80 border-l border-gray-200 bg-gray-50 flex flex-col">
              {/* Filter Toggle */}
              <div className="p-3 border-b border-gray-200 bg-white">
                <button
                  onClick={() => setFilterOpenWindow(!filterOpenWindow)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    filterOpenWindow
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {filterOpenWindow ? 'חלונות פתוחים בלבד' : 'כל השיחות'}
                  </span>
                  {openWindowCount > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      filterOpenWindow ? 'bg-green-200 text-green-800' : 'bg-green-500 text-white'
                    }`}>
                      {openWindowCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-gray-600 font-medium">
                    {filterOpenWindow ? 'אין שיחות עם חלון פתוח' : 'אין הודעות נכנסות'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    {filterOpenWindow ? 'נסה להציג את כל השיחות' : 'הודעות שיתקבלו יופיעו כאן'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredConversations.map((conversation) => (
                    <button
                      key={conversation.senderNumber}
                      onClick={() => handleConversationClick(conversation)}
                      className={`w-full px-4 py-3 text-right hover:bg-white transition-colors ${
                        selectedConversation?.senderNumber === conversation.senderNumber
                          ? 'bg-white border-l-4 border-pink-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-pink-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {conversation.profileName || formatPhoneNumber(conversation.senderNumber)}
                            </h3>
                            <span className="text-xs text-gray-500 mr-2">
                              {formatTime(conversation.lastMessage.createdAt)}
                            </span>
                          </div>
                          {conversation.profileName && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                              <Phone className="w-3 h-3" />
                              <span>{formatPhoneNumber(conversation.senderNumber)}</span>
                            </div>
                          )}
                          <p className="text-sm text-gray-600 truncate">
                            {conversation.lastMessage.messageBody}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500">
                              {conversation.totalMessages} הודעות
                            </span>
                            {/* 24-hour window indicator */}
                            {isWithin24HourWindow(conversation.lastIncomingMessageAt) && (
                              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                חלון פתוח
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              </div>
            </div>

            {/* Messages View */}
            <div className="flex-1 flex flex-col bg-[#E5DDD5]">
              {!selectedConversation ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      בחר שיחה להצגה
                    </h3>
                    <p className="text-gray-500">
                      לחץ על שיחה מהרשימה כדי לראות את ההודעות
                    </p>
                  </div>
                </div>
              ) : loadingMessages ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Conversation Header */}
                  <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-gray-900">
                          {selectedConversation.profileName || formatPhoneNumber(selectedConversation.senderNumber)}
                        </h2>
                        {selectedConversation.profileName && (
                          <p className="text-sm text-gray-500">
                            {formatPhoneNumber(selectedConversation.senderNumber)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {messageGroups.map((group, groupIndex) => (
                      <div key={groupIndex}>
                        {/* Date Separator */}
                        <div className="flex justify-center my-4">
                          <div className="bg-white px-3 py-1 rounded-lg shadow-sm">
                            <span className="text-xs text-gray-600">
                              {formatMessageDate(group.messages[0].createdAt)}
                            </span>
                          </div>
                        </div>

                        {/* Messages in this date group */}
                        {group.messages.map((message) => {
                          // Determine header type based on media
                          let headerType = 'none';
                          let headerMediaUrl = '';
                          let messageText = message.messageBody;

                          if (message.mediaUrl) {
                            if (message.mediaContentType?.startsWith('image/')) {
                              headerType = 'image';
                              headerMediaUrl = message.mediaUrl;
                            } else if (message.mediaContentType?.startsWith('video/')) {
                              headerType = 'video';
                              headerMediaUrl = message.mediaUrl;
                            } else {
                              // For other media types, add link to message text
                              messageText = `${messageText}\n\n📎 קובץ מצורף: ${message.mediaUrl}`;
                            }
                          }

                          // Determine alignment based on message direction
                          // outbound = sent by us (right), inbound = received from customer (left)
                          const isOutbound = message.direction === 'outbound';

                          // Format timestamp for display
                          const messageTime = new Date(message.createdAt).toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit'
                          });

                          return (
                            <WhatsAppMessagePreview
                              key={message.id}
                              headerType={headerType}
                              headerMediaUrl={headerMediaUrl}
                              text={messageText}
                              showContainer={false}
                              alignment={isOutbound ? 'right' : 'left'}
                              profileName={isOutbound ? null : message.profileName}
                              status={isOutbound ? message.status : null}
                              timestamp={messageTime}
                            />
                          );
                        })}
                      </div>
                    ))}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input Area */}
                  <div className="bg-[#F0F0F0] border-t border-gray-200 px-4 py-3">
                    {isWithin24HourWindow(selectedConversation.lastIncomingMessageAt) ? (
                      <>
                        {/* 24-hour window status indicator */}
                        <div className="flex items-center gap-2 mb-2 text-xs text-green-600">
                          <Clock className="w-3 h-3" />
                          <span>
                            חלון הודעות פתוח - נותרו {getRemainingWindowTime(selectedConversation.lastIncomingMessageAt)}
                          </span>
                        </div>

                        {/* Input and Send Button */}
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <textarea
                              value={messageInput}
                              onChange={(e) => setMessageInput(e.target.value)}
                              onKeyDown={handleKeyDown}
                              placeholder="הקלד הודעה..."
                              className="w-full px-4 py-2 rounded-2xl border border-gray-300 focus:outline-none focus:border-pink-500 resize-none text-right"
                              rows={1}
                              style={{ minHeight: '40px', maxHeight: '120px' }}
                            />
                          </div>
                          <button
                            onClick={handleSendMessage}
                            disabled={!messageInput.trim()}
                            className={`p-2 rounded-full transition-colors ${
                              messageInput.trim()
                                ? 'bg-pink-500 text-white hover:bg-pink-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      /* Window expired message */
                      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium">חלון ה-24 שעות פג תוקף</p>
                          <p className="text-xs mt-1">
                            לא ניתן לשלוח הודעות חופשיות. יש להמתין להודעה חדשה מהלקוח או לשלוח הודעת תבנית.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    </ContentLayout>
  );
}
